import { IconButton } from '@src/common/components/IconButton';
import { Menu } from '@src/common/components/Menu';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { CancelAction } from '@src/orders/components/orderActionsMenu/CancelAction';
import { ChangeRequestAction } from '@src/orders/components/orderActionsMenu/ChangeRequestAction';
import { DeleteAction } from '@src/orders/components/orderActionsMenu/DeleteAction';
import { DuplicateAction } from '@src/orders/components/orderActionsMenu/DuplicateAction';
import { EditAction } from '@src/orders/components/orderActionsMenu/EditAction';
import { ForceToReviewAction } from '@src/orders/components/orderActionsMenu/ForceToReviewAction';
import { GeneratePdfAction } from '@src/orders/components/orderActionsMenu/GeneratePdfAction';
import { PutOnHoldAction } from '@src/orders/components/orderActionsMenu/PutOnHoldAction';
import { ReopenAction } from '@src/orders/components/orderActionsMenu/ReopenAction';
import { ReviewAction } from '@src/orders/components/orderActionsMenu/ReviewAction';
import { UpdateInvoiceManuallyAction } from '@src/orders/components/orderActionsMenu/UpdateInvoiceManuallyAction';
import { ViewInvoiceAction } from '@src/orders/components/orderActionsMenu/ViewInvoiceAction';
import { ViewOrderAction } from '@src/orders/components/orderActionsMenu/ViewOrderAction';
import type { OrderListing } from '@src/orders/domain/OrderList';
import { OrderActions } from '@src/orders/domain/OrderList';
import { DotsVertical } from '@untitled-ui/icons-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const menuPossibleActions = [
  OrderActions.DELETE,
  OrderActions.EDIT,
  OrderActions.CANCEL,
  OrderActions.TOGGLE_ON_HOLD,
  OrderActions.REVIEW,
  OrderActions.MODIFY_INVOICE,
  OrderActions.VIEW_INVOICE,
  OrderActions.REOPEN,
  OrderActions.FORCE_TO_REVIEW,
  OrderActions.VIEW,
  OrderActions.DUPLICATE_ORDER,
  OrderActions.CHANGE_REQUEST,
  OrderActions.GENERATE_PDF,
];

type Props = {
  order: OrderListing;
  onStatusChange: () => void;
  onDeleteSuccess: () => void;
};
export const OrderActionsMenu = ({
  order,
  onStatusChange,
  onDeleteSuccess,
}: Props) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const hasMenuActions =
    menuPossibleActions.filter((action) => order.canDo(action)).length > 0;

  return (
    hasMenuActions && (
      <>
        <IconButton
          className={anchorEl ? openActionMenuStyle : actionMenuStyle}
          onClick={handleMenuClick}
          aria-label={t('actions')}
        >
          <DotsVertical
            width={24}
            height={24}
            color={theme.palette.primary.primary}
          />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <EditAction order={order} />
          <ViewOrderAction order={order} />
          <PutOnHoldAction order={order} onStatusChange={onStatusChange} />
          <ReviewAction order={order} />
          <ViewInvoiceAction order={order} />
          <UpdateInvoiceManuallyAction order={order} />
          <DuplicateAction order={order} />
          <ChangeRequestAction order={order} />
          <DeleteAction order={order} onDeleteSuccess={onDeleteSuccess} />
          <GeneratePdfAction order={order} />
          <CancelAction order={order} onStatusChange={onStatusChange} />
          <ReopenAction order={order} onStatusChange={onStatusChange} />
          <ForceToReviewAction order={order} onStatusChange={onStatusChange} />
        </Menu>
      </>
    )
  );
};

const openActionMenuStyle = css({
  backgroundColor: theme.palette.primary.primary,
  svg: {
    color: theme.palette.primary.onPrimary,
  },
});
const actionMenuStyle = css({
  backgroundColor: theme.palette.surface.surfaceContainer,
  '&:hover': {
    backgroundColor: theme.palette.primary.onPrimary,
  },
});
